import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AuthUser {
  email: string;
  idToken: string;
  accessToken: string;
  userId: string;   // admin invites use custom:kon-userId; self-signup uses Cognito sub
  role: string; 
}

// Returned when Cognito requires a new password (first-time login)
export interface NewPasswordChallenge {
  code: 'NewPasswordRequired';
  cognitoUser: CognitoUser;
  userAttributes: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userPool: CognitoUserPool;

  private authStateSubject = new BehaviorSubject<AuthUser | null>(this.loadFromStorage());
  public authState$: Observable<AuthUser | null> = this.authStateSubject.asObservable();

  constructor(private router: Router) {
    this.userPool = new CognitoUserPool({
      UserPoolId: environment.cognito.userPoolId,
      ClientId:   environment.cognito.clientId,
    });
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  // Resolves with AuthUser on success
  // Rejects with NewPasswordChallenge when first-time login requires password change
  // Rejects with { code, message } on auth failure
  login(email: string, password: string): Promise<AuthUser | NewPasswordChallenge> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });

      cognitoUser.authenticateUser(
        new AuthenticationDetails({ Username: email, Password: password }),
        {
          onSuccess: (session: CognitoUserSession) => {
            const user = this.sessionToUser(email, session);
            this.saveToStorage(user);
            this.authStateSubject.next(user);
            resolve(user);
          },
          onFailure: (err) => reject(this.mapError(err)),

          // Cognito sends this when an admin-created user logs in for the first time
          newPasswordRequired: (userAttributes, _requiredAttributes) => {
            // Remove non-writable attributes Cognito sends back
            delete userAttributes.email_verified;
            delete userAttributes.email;
            resolve({
              code: 'NewPasswordRequired',
              cognitoUser,
              userAttributes,
            } as NewPasswordChallenge);
          },
        }
      );
    });
  }

  // ── Complete new-password challenge (first-time login) ────────────────────
  completeNewPassword(
    cognitoUser: CognitoUser,
    newPassword: string,
    userAttributes: any
  ): Promise<AuthUser> {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (session: CognitoUserSession) => {
          const email = session.getIdToken().payload['email'] ?? '';
          const user  = this.sessionToUser(email, session);
          this.saveToStorage(user);
          this.authStateSubject.next(user);
          resolve(user);
        },
        onFailure: (err) => reject(this.mapError(err)),
      });
    });
  }

  // ── Forgot password — send verification code ──────────────────────────────
  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(this.mapError(err)),
      });
    });
  }

  // ── Organization-scoped self-registration ────────────────────────────────
  signUpOrganizationMember(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    orgToken: string
  ): Promise<void> {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
    ];
    return new Promise((resolve, reject) => {
      this.userPool.signUp(email, password, attributes, [], (err) => {
        if (err) { reject(this.mapError(err)); return; }
        resolve();
      }, { orgToken });
    });
  }

  confirmOrganizationMember(email: string, code: string, orgToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) { reject(this.mapError(err)); return; }
        resolve();
      }, { orgToken });
    });
  }

  resendOrganizationConfirmation(email: string, orgToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
      cognitoUser.resendConfirmationCode((err) => {
        if (err) { reject(this.mapError(err)); return; }
        resolve();
      }, { orgToken });
    });
  }

  // ── Forgot password — confirm code + set new password ────────────────────
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(this.mapError(err)),
      });
    });
  }

  // ── Logout ────────────────────────────────────────────────────────────────
  logout(): void {
    this.userPool.getCurrentUser()?.signOut();
    this.clearStorage();
    this.authStateSubject.next(null);
    this.router.navigate(['/login']);
  }

  // ── Session ───────────────────────────────────────────────────────────────
  isLoggedIn(): boolean {
    return !!this.authStateSubject.getValue();
  }

  getIdToken(): string | null {
    return this.authStateSubject.getValue()?.idToken ?? null;
  }

  getCurrentUser(): AuthUser | null {
    return this.authStateSubject.getValue();
  }

  refreshSession(): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
      if (!cognitoUser) { reject('No user'); return; }
      cognitoUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) { this.logout(); reject(err); return; }
        const email = session.getIdToken().payload['email'] ?? '';
        const user  = this.sessionToUser(email, session);
        this.saveToStorage(user);
        this.authStateSubject.next(user);
        resolve(user.idToken);
      });
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
 private sessionToUser(email: string, session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().payload;
  console.log('Session payload:', payload); // Debug log to inspect token contents
  return {
    email,
    idToken:     session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    userId:      payload['custom:kon-userId'] ?? payload['sub'] ?? '',
    role:        payload['custom:role']   ?? 'member',
  };
}

  private saveToStorage(user: AuthUser): void {
    localStorage.setItem('konvac_user', JSON.stringify(user));
    localStorage.setItem('userId', user.userId);
    localStorage.setItem('role', user.role);
  }

  private loadFromStorage(): AuthUser | null {
    try {
      const raw = localStorage.getItem('konvac_user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  private clearStorage(): void {
    localStorage.removeItem('konvac_user');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
  }

  private mapError(err: any): { code: string; message: string } {
    const map: Record<string, string> = {
      NotAuthorizedException:          'Incorrect email or password.',
      UserNotFoundException:           'No account found with this email.',
      UserNotConfirmedException:       'Please verify your email before logging in.',
      PasswordResetRequiredException:  'A password reset is required.',
      CodeMismatchException:           'Invalid verification code. Please try again.',
      ExpiredCodeException:            'Verification code has expired. Please request a new one.',
      InvalidPasswordException:        'Password does not meet requirements.',
      LimitExceededException:          'Too many attempts. Please try again later.',
      UsernameExistsException:         'An account with this email already exists. Sign in to continue.',
      InvalidParameterException:       'Please check the registration details and try again.',
      CodeDeliveryFailureException:    'We could not send the verification code. Please try again.',
    };
    return {
      code:    err.code ?? 'UnknownError',
      message: map[err.code] ?? err.message ?? 'Something went wrong. Please try again.',
    };
  }
}
